/**
 * Signing in to an existing account.
 *
 * Deliberately does NOT create an account on a miss: a sign-in that quietly
 * registers a mistyped address looks, to the person it happens to, like their
 * password stopped working.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signInWithEmail } from '../net/auth.js';
import { useSession } from '../session-context.js';
import { describeError } from './errors.js';

export function Login() {
  const { adopt } = useSession();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    setError(null);
    try {
      await adopt(await signInWithEmail(email, password));
      navigate('/library');
    } catch (caught) {
      // One message for a wrong password and for no such account: saying which
      // is a way to find out who has registered.
      setError(describeError(caught, 'That email and password do not match.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="page narrow" onSubmit={submit}>
      <h1 className="page-title">Log in</h1>

      <label className="field">
        <span className="field-label">Email</span>
        <input
          className="text-input"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="email"
          autoCapitalize="none"
          autoCorrect="off"
          required
        />
      </label>

      <label className="field">
        <span className="field-label">Password</span>
        <input
          className="text-input"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          required
        />
      </label>

      {error ? <p className="banner error">{error}</p> : null}

      <button type="submit" className="big-button primary" disabled={busy}>
        {busy ? 'Checking…' : 'Log in'}
      </button>

      <div className="page-links">
        <Link className="text-button" to="/reset">
          Forgot your password?
        </Link>
        <Link className="text-button" to="/register">
          Create an account
        </Link>
        <Link className="text-button" to="/">
          Back
        </Link>
      </div>
    </form>
  );
}
